import React from 'react';
import { Box, Divider, Slider, Typography, alpha } from '@mui/material';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import WeekendIcon from '@mui/icons-material/Weekend';

const ACCENT = '#4ce1b6';
const MIN = 1;
const MAX = 2;
const STEP = 0.05;

function parseMultiplier(value) {
  const number = Number(value);
  if (!Number.isFinite(number) || number <= 0) return null;
  return number;
}

function sliderValue(value) {
  const factor = parseMultiplier(value) ?? 1;
  return Math.min(MAX, Math.max(MIN, Math.round(factor / STEP) * STEP));
}

function formatFactor(value) {
  const factor = sliderValue(value);
  return Number.isInteger(factor) ? `${factor.toFixed(1)}x` : `${factor.toFixed(2)}x`;
}

const sliderSx = (isDark, active) => ({
  color: ACCENT,
  height: 5,
  py: 0.5,
  '& .MuiSlider-rail': {
    opacity: 1,
    backgroundColor: isDark ? alpha(ACCENT, 0.2) : '#c8e6c9',
    borderRadius: 999,
  },
  '& .MuiSlider-track': {
    border: 'none',
    backgroundColor: active ? ACCENT : alpha(ACCENT, 0.7),
    borderRadius: 999,
  },
  '& .MuiSlider-thumb': {
    width: 14,
    height: 14,
    backgroundColor: '#fff',
    border: `2px solid ${isDark ? ACCENT : '#2e7d32'}`,
    boxShadow: active ? `0 0 0 4px ${alpha(ACCENT, 0.18)}` : 'none',
    '&:hover, &.Mui-focusVisible': {
      boxShadow: `0 0 0 6px ${alpha(ACCENT, 0.22)}`,
    },
    '&::before': { boxShadow: 'none' },
  },
});

function MultiplierCell({ title, days, icon, value, onChange, isDark }) {
  const factor = sliderValue(value);
  const active = factor !== 1;

  return (
    <Box sx={{ flex: 1, minWidth: 0, px: 1.25, py: 0.85 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.35 }}>
        <Box
          sx={{
            width: 22,
            height: 22,
            borderRadius: '7px',
            display: 'grid',
            placeItems: 'center',
            background: `linear-gradient(160deg, ${alpha(ACCENT, isDark ? 0.28 : 0.28)} 0%, ${alpha(ACCENT, 0.08)} 100%)`,
            color: isDark ? ACCENT : '#0f766e',
            flexShrink: 0,
          }}
        >
          {icon}
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="caption" sx={{ fontWeight: 800, lineHeight: 1.15, display: 'block' }}>
            {title}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.62rem', lineHeight: 1 }}>
            {days}
          </Typography>
        </Box>
        <Box
          sx={{
            px: 0.75,
            py: 0.15,
            borderRadius: 999,
            bgcolor: active ? ACCENT : alpha(ACCENT, isDark ? 0.16 : 0.18),
            color: active ? '#04120e' : isDark ? ACCENT : '#0f766e',
            fontSize: '0.72rem',
            fontWeight: 800,
            lineHeight: 1.4,
            minWidth: 38,
            textAlign: 'center',
          }}
        >
          {formatFactor(value)}
        </Box>
      </Box>

      <Slider
        value={factor}
        min={MIN}
        max={MAX}
        step={STEP}
        onChange={(_, next) => onChange(Number(next).toFixed(2))}
        sx={sliderSx(isDark, active)}
      />
    </Box>
  );
}

export function parseDayMultiplier(value) {
  if (value === '' || value == null) return 1;
  return parseMultiplier(value) ?? 1;
}

export function isValidDayMultiplier(value) {
  if (value === '' || value == null) return true;
  return parseMultiplier(value) != null;
}

export default function RateDayMultiplierCards({
  weekdayValue,
  weekendValue,
  onWeekdayChange,
  onWeekendChange,
  daySelectionType = 'custom',
  isDark = false,
}) {
  const showWeekdays = daySelectionType !== 'weekend';
  const showWeekend = daySelectionType !== 'weekdays';

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 0.75, fontWeight: 700 }}>
        Day multipliers
      </Typography>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'stretch',
          borderRadius: 2,
          overflow: 'hidden',
          border: `1px solid ${alpha(ACCENT, isDark ? 0.35 : 0.4)}`,
          background: isDark
            ? `linear-gradient(135deg, ${alpha(ACCENT, 0.1)} 0%, ${alpha('#000', 0.12)} 100%)`
            : `linear-gradient(135deg, ${alpha(ACCENT, 0.12)} 0%, #ffffff 62%)`,
          boxShadow: `0 6px 16px ${alpha(ACCENT, 0.1)}`,
        }}
      >
        {showWeekdays && (
          <MultiplierCell
            title="Weekdays"
            days="Mon–Fri"
            icon={<WorkOutlineIcon sx={{ fontSize: 14 }} />}
            value={weekdayValue}
            onChange={onWeekdayChange}
            isDark={isDark}
          />
        )}
        {showWeekdays && showWeekend && (
          <Divider
            orientation="vertical"
            flexItem
            sx={{ borderColor: alpha(ACCENT, isDark ? 0.28 : 0.32) }}
          />
        )}
        {showWeekend && (
          <MultiplierCell
            title="Weekend"
            days="Sat–Sun"
            icon={<WeekendIcon sx={{ fontSize: 14 }} />}
            value={weekendValue}
            onChange={onWeekendChange}
            isDark={isDark}
          />
        )}
      </Box>
    </Box>
  );
}
